/**
 * Bundle Products Block - Horizon-integrated
 * - Single /cart/add.js call with items[]
 * - On success: dispatches cart:update (same as product-form-component)
 * - No page reload, no section reload hacks
 */

class BundleProducts {
  constructor(container) {
    this.container = container;
    if (!this.container) return;

    this.bundleItems = this.container.querySelectorAll('[data-bundle-item]');
    this.addToCartBtn = this.container.querySelector('[data-bundle-add-btn]');
    this.btnText = this.container.querySelector('[data-btn-text]');
    this.statusContainer = this.container.querySelector('[data-selection-status]');
    this.statusText = this.container.querySelector('[data-status-text]');

    this.selections = new Map();
    this.productVariants = new Map();

    this.init();
  }

  /* ---------- Init ---------- */

  init() {
    this.bundleItems.forEach((item) => {
      const productId = item.dataset.productId;
      const variantsScript = item.querySelector(
        `[data-product-variants="${productId}"]`
      );

      if (variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent);
          this.productVariants.set(productId, variants);
        } catch (e) {
          console.error('[Bundle] Error parsing variants JSON:', e);
        }
      }

      const variantInput = item.querySelector('[data-bundle-variant-input]');
      if (!variantInput) return;

      const hasOptions = !!item.querySelector('.bundle-product-variants');

      this.selections.set(productId, {
        variantId: variantInput.value,
        selected: !hasOptions, // products with only default variant are auto-selected
        hasOnlyDefaultVariant: !hasOptions
      });
    });

    this.bindEvents();
    this.updateUI();
  }

  bindEvents() {
    // Variant buttons
    this.container.addEventListener('click', (e) => {
      const variantBtn = e.target.closest('.bundle-variant-btn');
      if (variantBtn && !variantBtn.disabled) {
        this.handleVariantSelect(variantBtn);
      }
    });

    // Bundle ATC
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    // Accordions (only one open at a time)
    this.container
      .querySelectorAll('.bundle-product-accordion')
      .forEach((accordion) => {
        accordion.addEventListener('toggle', (e) => {
          if (!e.target.open) return;

          this.container
            .querySelectorAll('.bundle-product-accordion')
            .forEach((other) => {
              if (other !== e.target) other.removeAttribute('open');
            });
        });
      });
  }

  /* ---------- Variant selection ---------- */

  handleVariantSelect(btn) {
    const productId = btn.dataset.productId;
    const optionPosition = parseInt(btn.dataset.optionPosition, 10);
    const optionValue = btn.dataset.optionValue;
    const bundleItem = btn.closest('[data-bundle-item]');

    if (!productId || !bundleItem || !optionPosition) return;

    // Toggle selected state in this option group
    const optionGroup = btn.closest('.bundle-variant-options');
    optionGroup
      .querySelectorAll('.bundle-variant-btn')
      .forEach((b) => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    // Build selected options array
    const selectedOptions = this.getSelectedOptions(bundleItem);
    selectedOptions[optionPosition - 1] = optionValue;

    const variants = this.productVariants.get(productId);
    if (variants && variants.length) {
      const matchingVariant = this.findMatchingVariant(variants, selectedOptions);

      if (matchingVariant) {
        // sync hidden input
        const variantInput = bundleItem.querySelector(
          '[data-bundle-variant-input]'
        );
        if (variantInput) {
          variantInput.value = matchingVariant.id;
        }

        // update selection map
        const selection = this.selections.get(productId);
        if (selection) {
          selection.variantId = matchingVariant.id;
          selection.selected = this.areAllOptionsSelected(bundleItem);
        }

        // update inline price UI
        this.updateProductPrice(bundleItem, matchingVariant);

        // update disabled/enabled options
        this.updateAvailableOptions(bundleItem, variants, selectedOptions);
      }
    }

    this.updateUI();
  }

  getSelectedOptions(bundleItem) {
    const options = [];
    bundleItem
      .querySelectorAll('.bundle-variant-options')
      .forEach((group, index) => {
        const selected = group.querySelector('.bundle-variant-btn.is-selected');
        options[index] = selected ? selected.dataset.optionValue : null;
      });
    return options;
  }

  areAllOptionsSelected(bundleItem) {
    const groups = bundleItem.querySelectorAll('.bundle-variant-options');
    for (let i = 0; i < groups.length; i++) {
      if (!groups[i].querySelector('.bundle-variant-btn.is-selected')) {
        return false;
      }
    }
    return true;
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) =>
      selectedOptions.every((option, index) => {
        if (option === null) return true;
        const key = 'option' + (index + 1);
        return variant[key] === option;
      })
    );
  }

  updateAvailableOptions(bundleItem, variants, selectedOptions) {
    const optionGroups = bundleItem.querySelectorAll('.bundle-variant-options');

    optionGroups.forEach((group, groupIndex) => {
      const position = groupIndex + 1;

      group.querySelectorAll('.bundle-variant-btn').forEach((btn) => {
        const value = btn.dataset.optionValue;

        const isAvailable = variants.some((variant) => {
          if (!variant.available) return false;

          const matchesValue = variant['option' + position] === value;

          const matchesOthers = selectedOptions.every((selected, idx) => {
            if (idx === groupIndex || selected === null) return true;
            return variant['option' + (idx + 1)] === selected;
          });

          return matchesValue && matchesOthers;
        });

        btn.disabled = !isAvailable;
        btn.classList.toggle('is-disabled', !isAvailable);
      });
    });
  }

  updateProductPrice(bundleItem, variant) {
    const priceEl = bundleItem.querySelector('.bundle-product-price--current');
    const compareEl = bundleItem.querySelector('.bundle-product-price--compare');

    if (priceEl && typeof variant.price === 'number') {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.innerHTML = `<s>${this.formatMoney(
          variant.compare_at_price
        )}</s>`;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  /* ---------- UI state (button + status) ---------- */

  updateUI() {
    const totalProducts = this.selections.size;
    let selectedCount = 0;

    this.selections.forEach((selection, productId) => {
      const item = this.container.querySelector(
        `[data-product-id="${productId}"]`
      );

      if (selection.selected || selection.hasOnlyDefaultVariant) {
        selectedCount++;
        if (item) item.classList.add('is-selected');

        const toggleText = item
          ? item.querySelector('[data-toggle-text]')
          : null;
        if (toggleText) toggleText.textContent = 'Selected ✓';
      } else {
        if (item) item.classList.remove('is-selected');

        const toggleText = item
          ? item.querySelector('[data-toggle-text]')
          : null;
        if (toggleText) toggleText.textContent = 'Select option';
      }
    });

    const allSelected = selectedCount === totalProducts;

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !allSelected;
      this.addToCartBtn.classList.toggle('is-ready', allSelected);
    }

    if (this.btnText) {
      this.btnText.textContent = allSelected
        ? 'Add Bundle to Cart'
        : 'Select all options';
    }

    if (this.statusContainer) {
      this.statusContainer.classList.toggle('is-complete', allSelected);
    }

    if (this.statusText) {
      if (allSelected) {
        this.statusText.textContent = `All ${totalProducts} products selected`;
      } else {
        const remaining = totalProducts - selectedCount;
        this.statusText.textContent =
          'Select options for ' +
          remaining +
          ' more product' +
          (remaining > 1 ? 's' : '');
      }
    }
  }

  /* ---------- Single add-to-cart call + horizon cart:update ---------- */

  async handleAddToCart() {
    if (!this.addToCartBtn || this.addToCartBtn.disabled) return;

    // Collect selected variant IDs
    const items = [];
    this.selections.forEach((selection) => {
      if ((selection.selected || selection.hasOnlyDefaultVariant) && selection.variantId) {
        items.push({
          id: Number(selection.variantId),
          quantity: 1
        });
      }
    });

    if (!items.length) return;

    const btnLoading = this.container.querySelector('.bundle-add-btn-loading');

    // UI → loading
    if (this.btnText) this.btnText.textContent = 'Adding bundle...';
    if (btnLoading) btnLoading.classList.remove('hidden');
    this.addToCartBtn.disabled = true;

    try {
      const body = JSON.stringify({ items });

      let root = '/';
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        root = window.Shopify.routes.root;
      }

      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Accept: 'application/json'
        },
        body
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        console.error('[Bundle] Add to cart error:', data);
        if (this.btnText) this.btnText.textContent = 'Error — try again';
      } else {
        const totalAdded = items.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0
        );

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              data: {
                itemCount: totalAdded,
                source: 'product-form-component' // same source as native ATC
              }
            }
          })
        );

        if (this.btnText) this.btnText.textContent = 'Bundle added ✓';
      }
    } catch (err) {
      console.error('[Bundle] Add to cart failed:', err);
      if (this.btnText) this.btnText.textContent = 'Error — try again';
    } finally {
      if (btnLoading) btnLoading.classList.add('hidden');
      setTimeout(() => {
        if (this.btnText) this.btnText.textContent = 'Add Bundle to Cart';
        this.addToCartBtn.disabled = false;
      }, 1500);
    }
  }
}

/* ---------- Init helpers ---------- */

function initBundleBlocks() {
  document
    .querySelectorAll('.bundle-products-block')
    .forEach((block) => new BundleProducts(block));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleBlocks);
} else {
  initBundleBlocks();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  section
    .querySelectorAll('.bundle-products-block')
    .forEach((block) => new BundleProducts(block));
});
